import React, { useState } from "react";
import { Droppable, Draggable, DragDropContext } from "react-beautiful-dnd";
import drag from "../../../assets/images/drag.svg";
import ph_question from "../../../assets/images/ph_question.svg";
import selected_question from "../../../assets/images/selected_question.svg";
import { Input, Select, Popover, Tooltip, notification } from "antd";
import trash from "../../../assets/images/trash.svg";
import { useTranslation } from "react-i18next";
import axios from "axios";
import environment from "environment";
import { forceLogout } from "utilities/HttpUtility";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  CATEGORY_OPTIONS,
  CATEGORY_CHANGE_REASON_OPTIONS,
  EXCLUSION_SUBCODE_OPTIONS,
  getCategoryThemeClass,
} from "../../../constants/categoryConstants";

const LineItem = (props) => {
  const { t } = useTranslation();
  const currentLanguage = useSelector((state) => state.common.currentLanguage);

  const {
    treatments,
    treatmentId,
    handleConfirmationPopup,
    handleOnChangeItem,
    isEdit,
    selectedPage,
    treatmentIndex,
    extracted_invoice_data
  } = props;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [description, setDescription] = useState(null);
  // const [prevItem, setPrevItem] = useState();
  const [selectedMedicine, setSelectedMedicine] = useState(null);
  const [openPopups, setOpenPopups] = useState({});
  const [customVAT, setCustomVAT] = useState({});
  const { id } = useParams();

  const enableItemClassification = environment.enableItemClassification;

  const getItemClassification = (item) => {
    const normalizeValue = (value) => {
      if (typeof value === "string") return value.trim();
      if (value === null || value === undefined) return "";
      return String(value).trim();
    };

    const rawCategory = item?.category ?? item?.Category ?? "";
    const category = normalizeValue(rawCategory);

    const rawExclusionSubcode =
      item?.exclusion_subcode ??
      item?.exclusionSubcode ??
      item?.exclusionSubCode ??
      item?.exclusion ??
      "";
    const exclusionSubcode = normalizeValue(rawExclusionSubcode);

    const rawReasoning = item?.reasoning ?? item?.Reasoning ?? "";
    const reasoning = normalizeValue(rawReasoning);

    const rawKeywords =
      item?.detection_keywords ?? item?.detectionKeywords ?? item?.keywords ?? [];
    const detectionKeywords = Array.isArray(rawKeywords) ? rawKeywords : [];

    const rawConfidence =
      item?.confidence_level ?? item?.confidenceLevel ?? "";
    const confidenceLevel = normalizeValue(rawConfidence).toLowerCase();

    return {
      category,
      exclusionSubcode,
      reasoning,
      detectionKeywords,
      confidenceLevel,
      feedbackOriginalCategory: normalizeValue(item?.feedback_original_category),
      feedbackOriginalExclusionSubcode: normalizeValue(
        item?.feedback_original_exclusion_subcode
      ),
      feedbackChangeReason: normalizeValue(item?.feedback_change_reason),
      feedbackChangeContext: item?.feedback_change_context ?? "",
    };
  };

  const hasClassificationOverride = ({
    category,
    exclusionSubcode,
    feedbackOriginalCategory,
    feedbackOriginalExclusionSubcode,
  }) =>
    category !== feedbackOriginalCategory ||
    exclusionSubcode !== feedbackOriginalExclusionSubcode;

  const getConfidenceToneClass = (confidenceLevel) => {
    if (confidenceLevel === "medium" || confidenceLevel === "low") {
      return `lineitem-ai-confidence-badge--${confidenceLevel}`;
    }

    return "lineitem-ai-confidence-badge--high";
  };

  const confidenceLabelMap = {
    high: "confidenceHigh",
    medium: "confidenceMedium",
    low: "confidenceLow",
  };

  const categorySelectOptions = CATEGORY_OPTIONS.map((opt) => ({
    value: opt.code,
    label: `${opt.code} - ${t(opt.translationKey)}`,
  }));

  const exclusionSelectOptions = EXCLUSION_SUBCODE_OPTIONS.map((opt) => ({
    value: opt.code,
    label: `${opt.code} - ${t(opt.translationKey)}`,
  }));

  const feedbackReasonOptions = CATEGORY_CHANGE_REASON_OPTIONS.map((opt) => ({
    value: opt.value,
    label: t(opt.translationKey),
  }));

  const handleCategoryChange = (value, itemKey) => {
    const cat = value ?? "";
    handleOnChangeItem(treatmentId, itemKey, "category", cat);
    if (cat !== "E") {
      handleOnChangeItem(treatmentId, itemKey, "exclusion_subcode", "");
    }
  };

  const handleExclusionSubcodeChange = (value, itemKey) => {
    handleOnChangeItem(treatmentId, itemKey, "exclusion_subcode", value ?? "");
  };

  const handleFeedbackReasonChange = (value, itemKey) => {
    handleOnChangeItem(treatmentId, itemKey, "feedback_change_reason", value ?? "");
  };

  const handleFeedbackContextChange = (event, itemKey) => {
    handleOnChangeItem(
      treatmentId,
      itemKey,
      "feedback_change_context",
      event.target.value
    );
  };

  const renderItemClassification = (item) => {
    if (!enableItemClassification) return null;

    const itemName = (item?.["Item Name"] ?? "").toString().trim();
    const {
      category,
      exclusionSubcode,
      reasoning,
      detectionKeywords,
      confidenceLevel,
      feedbackOriginalCategory,
      feedbackOriginalExclusionSubcode,
      feedbackChangeReason,
      feedbackChangeContext,
    } = getItemClassification(item);

    const normalizedKeywords = detectionKeywords
      .filter((keyword) => typeof keyword === "string" && keyword.trim().length > 0)
      .map((keyword) => keyword.trim());

    const hasAnyClassification =
      category.length > 0 ||
      exclusionSubcode.length > 0 ||
      reasoning.length > 0 ||
      normalizedKeywords.length > 0;

    if (!itemName && !hasAnyClassification) return null;

    const visibleKeywords = normalizedKeywords.slice(0, 3);
    const hiddenKeywords = normalizedKeywords.slice(3);
    const isClassificationChanged = hasClassificationOverride({
      category,
      exclusionSubcode,
      feedbackOriginalCategory,
      feedbackOriginalExclusionSubcode,
    });
    const confidenceLabelKey = confidenceLabelMap[confidenceLevel] || "";

    return (
      <div className={`lineitem-ai ${getCategoryThemeClass(category)}`}>
        <div className="lineitem-ai-top">
          <div className="lineitem-ai-categories">
            <div className="lineitem-ai-field">
              <span className="lineitem-ai-field-label">{t("category")}</span>
              <Select
                value={category || undefined}
                placeholder={t("unclassified")}
                onChange={(val) => handleCategoryChange(val, item.key)}
                disabled={isEdit}
                size="small"
                popupMatchSelectWidth={false}
                className="lineitem-ai-select"
                options={categorySelectOptions}
                allowClear
              />
            </div>

            {category === "E" ? (
              <div className="lineitem-ai-field lineitem-ai-field--narrow">
                <span className="lineitem-ai-field-label">{t("exclusionSubcode")}</span>
                <Select
                  value={exclusionSubcode || undefined}
                  placeholder="E-..."
                  onChange={(val) => handleExclusionSubcodeChange(val, item.key)}
                  disabled={isEdit}
                  size="small"
                  popupMatchSelectWidth={false}
                  className="lineitem-ai-select"
                  options={exclusionSelectOptions}
                  allowClear
                />
              </div>
            ) : null}

            {confidenceLabelKey ? (
              <Tooltip title={t(confidenceLabelKey)}>
                <div
                  className={`lineitem-ai-confidence-badge ${getConfidenceToneClass(confidenceLevel)}`}
                >
                  <span
                    className={`lineitem-ai-confidence lineitem-ai-confidence--${confidenceLevel}`}
                  />
                  <span className="lineitem-ai-confidence-text">
                    {t(confidenceLabelKey)}
                  </span>
                </div>
              </Tooltip>
            ) : null}
          </div>

          {visibleKeywords.length > 0 ? (
            <div className="lineitem-ai-keywords">
              {visibleKeywords.map((keyword) => (
                <span key={keyword} className="lineitem-ai-keyword">
                  {keyword}
                </span>
              ))}
              {hiddenKeywords.length > 0 ? (
                <Tooltip title={hiddenKeywords.join(", ")}>
                  <span className="lineitem-ai-keyword lineitem-ai-keyword--more">
                    +{hiddenKeywords.length}
                  </span>
                </Tooltip>
              ) : null}
            </div>
          ) : null}
        </div>

        {reasoning ? (
          <Tooltip title={reasoning}>
            <div className="lineitem-ai-reasoning">
              <span className="lineitem-ai-reasoning-label">{t("reasoning")}:</span>
              <span className="lineitem-ai-reasoning-text">{reasoning}</span>
            </div>
          </Tooltip>
        ) : null}

        {isClassificationChanged ? (
          <div className="lineitem-ai-feedback">
            <div className="lineitem-ai-field lineitem-ai-field--feedback">
              <span className="lineitem-ai-field-label lineitem-ai-field-label--required">
                {t("reasonForChange")}
              </span>
              <Select
                value={feedbackChangeReason || undefined}
                placeholder={t("reasonForChangePlaceholder")}
                onChange={(val) => handleFeedbackReasonChange(val, item.key)}
                disabled={isEdit}
                size="small"
                popupMatchSelectWidth={false}
                className="lineitem-ai-select"
                options={feedbackReasonOptions}
                allowClear
                status={!feedbackChangeReason && !isEdit ? "error" : undefined}
              />
            </div>

            {feedbackChangeReason ? (
              <div className="lineitem-ai-field lineitem-ai-field--feedback lineitem-ai-field--feedback-wide">
                <span className="lineitem-ai-field-label">{t("changeContext")}</span>
                <Input
                  value={feedbackChangeContext}
                  placeholder={t("changeContextPlaceholder")}
                  onChange={(event) => handleFeedbackContextChange(event, item.key)}
                  disabled={isEdit}
                  className="lineitem-ai-feedback-input"
                />
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  };

  const handleOnChangeVAT = (e, itemKey) => {
    handleOnChangeItem(treatmentId, itemKey, "VAT", e);
    setCustomVAT((prev) => ({
      ...prev,
      [itemKey]: e,
    }));
  };

  const handleCustomInputChange = (e, itemKey) => {
    const value = e.target.value.replace("%", "").trim();
    setCustomVAT((prev) => ({
      ...prev,
      [itemKey]: value,
    }));
    handleOnChangeVAT(value, itemKey);
  };

  const Popup = ({ itemId }) => {
    if (loading) {
      return (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "94px",
            width: "160px",
          }}
        >
          <div className="">
            <div className="popover-loader"></div>
            <div className="popover-loader-text">{t("loading")}</div>
          </div>
        </div>
      );
    }
    if (!data || !description) {
      return null;
    }
    return (
      <div className="col-md-12 text-start px-0 mt-3 position-relative">
        <div className="history-header">
          <h1 className="fs16 text-blue-a poppins-medium">{data}</h1>
          <button
            type="button"
            className="btn-close position-absolute top-0 end-0 m-3"
            aria-label="Close"
            onClick={() => handleClosePopup(itemId)}
            style={{
              filter: "brightness(80%)",
            }}
          ></button>
          <h3 className="fs12 cell-gray my-2 pb-1"> {t("medicineDescription")}</h3>
          <div
            style={{ borderBottom: "1px solid #EBEEF2", marginBottom: "10px" }}
          ></div>
          <div className="row">
            {Object.entries(description.medicine_description).map(([key, value]) => (
                <div className="col-sm-6" key={key}>
                <p className="fs12 dark-text poppins-medium mb-2">{t(`${key}`)}</p>
                  {Array.isArray(value) ? (
                    <ul>
                      {value.map((item, index) => (
                        <li className="fs11 medicine-description-popup-values" key={index}>
                          - {item}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="fs11 medicine-description-popup-values">- {value}</p>
                  )}
                </div>
              )
            )}
          </div>
        </div>
      </div>
    );
  };

  const fetchMedicineInfo = async (name, itemId) => {
    try {
      setLoading(true);
      let payload = {};
      let endpoint = `${environment.baseApi}/document/medicine`;
      payload = {
        id: id,
        extractedInvoiceData: extracted_invoice_data,
        pageNo: selectedPage - 1,
        treatmentOrder: treatmentIndex,
        itemOrder: itemId,
        medicineName: name,
        language: currentLanguage
      };
      const token = sessionStorage.getItem("authToken");

      const headers = {
        "Content-Type": "application/json",
      };
      if (token) {
        headers.Authorization = token;
      }
      const response = await axios.post(endpoint, payload, {
        headers,
      });
      if (response.status !== 200) {
        if (response.status === 401) {
          notification.error({
            message: t("accessDenied"),
            description: t("tokenExpired"),
          });
          forceLogout(response.status);
        } else {
          notification.error({
            message: "Something went wrong",
          });
        }
      }
      setLoading(false);
      if (!response || response.data.result.error) {
        throw new Error(
          "Invalid response data or missing medicine description"
        );
      }
      let result = {
        medicine_description: {},
      };
      result.medicine_description["purpose"] =
        response.data.result.medicine_description.purpose;
      result.medicine_description["usedFor"] =
        response.data.result.medicine_description.used_for;
      result.medicine_description["petTypes"] =
        response.data.result.medicine_description.pet_types;
      result.medicine_description["activeIngredients"] =
        response.data.result.medicine_description.active_ingredients;

      if (enableItemClassification) {
        result.medicine_description["category"] =
          response.data.result.medicine_description.category;
        result.medicine_description["typeOfDamage"] =
          response.data.result.medicine_description.type_of_damage;
        result.medicine_description["causeOfDamage"] =
          response.data.result.medicine_description.cause_of_damage;
        
        // Extract item classification data from extracted_invoice_data
        const currentPageData = extracted_invoice_data[selectedPage - 1];
        if (currentPageData && currentPageData.treatments && currentPageData.treatments[treatmentIndex]) {
          const currentTreatment = currentPageData.treatments[treatmentIndex];
          if (currentTreatment.Items && currentTreatment.Items[itemId]) {
            const itemData = currentTreatment.Items[itemId];
            result.medicine_description["category"] = itemData.category || "";
            result.medicine_description["reasoning"] = itemData.reasoning || "";
            result.medicine_description["detection_keywords"] = itemData.detection_keywords || [];
          }
        }
      }

      setDescription(result);
    } catch (error) {
      console.error("Error fetching medicine information:", error);
      setLoading(false);
    }
  };

  const reorderItems = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  const handleDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) {
      return;
    }
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return;
    }
    // Reorder items
    let reordered = reorderItems(
      treatments.Items,
      source.index,
      destination.index
    );
    // Update item_order based on new order
    reordered = reordered.map((item, idx) => ({
      ...item,
      item_order: idx + 1,
    }));
    treatments.Items = reordered;
  };

  const invalidChars = [
    "+",
    "e",
    "E",
    ",",
    " ",
    "!",
    "@",
    "#",
    "$",
    "%",
    "^",
    "&",
    "*",
    "(",
    ")",
    "_",
    "=",
    "{",
    "}",
    "[",
    "]",
    "|",
    "\\",
    ":",
    ";",
    "'",
    '"',
    "<",
    ">",
    "?",
    "/",
    "~",
    "`",
    "a",
    "b",
    "c",
    "d",
    "f",
    "g",
    "h",
    "i",
    "j",
    "k",
    "l",
    "m",
    "n",
    "o",
    "p",
    "q",
    "r",
    "s",
    "t",
    "u",
    "v",
    "w",
    "x",
    "y",
    "z",
    "A",
    "B",
    "C",
    "D",
    "F",
    "G",
    "H",
    "I",
    "J",
    "K",
    "L",
    "M",
    "N",
    "O",
    "P",
    "Q",
    "R",
    "S",
    "T",
    "U",
    "V",
    "W",
    "X",
    "Y",
    "Z",
  ];

  const handleKeyDown = (e) => {
    if (invalidChars.includes(e.key)) {
      e.preventDefault();
    }
  };

  const handleInputKeyDown = (e) => {
    const invalidChars = [
      "+",
      "e",
      "E",
      ",",
      " ",
      "!",
      "@",
      "#",
      "$",
      "%",
      "^",
      "&",
      "*",
      "(",
      ")",
      "_",
      "=",
      "{",
      "}",
      "[",
      "]",
      "|",
      "\\",
      ":",
      ";",
      "'",
      '"',
      "<",
      ">",
      "?",
      "/",
      "~",
      "`",
      "a",
      "b",
      "c",
      "d",
      "f",
      "g",
      "h",
      "i",
      "j",
      "k",
      "l",
      "m",
      "n",
      "o",
      "p",
      "q",
      "r",
      "s",
      "t",
      "u",
      "v",
      "w",
      "x",
      "y",
      "z",
      "A",
      "B",
      "C",
      "D",
      "F",
      "G",
      "H",
      "I",
      "J",
      "K",
      "L",
      "M",
      "N",
      "O",
      "P",
      "Q",
      "R",
      "S",
      "T",
      "U",
      "V",
      "W",
      "X",
      "Y",
      "Z",
    ];
    console.log(invalidChars.includes(e.key), "invalidChars.includes(e.key)==");
    if (invalidChars.includes(e.key)) {
      e.preventDefault();
    }
  };

  const handleClosePopup = (itemId) => {
    setSelectedMedicine(null);
    setOpenPopups((prevState) => ({
      ...prevState,
      [itemId]: false,
    }));
  };

  const handleImageClick = (itemId, itemName) => {
    setSelectedMedicine(itemId);
    setOpenPopups((prevState) => ({
      ...prevState,
      [itemId]: true,
    }));
    setData(itemName);
    fetchMedicineInfo(itemName, itemId);
  };

  return (
    <>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId={String(treatmentId)} type="line-item">
          {(provided) => (
            <div ref={provided.innerRef}>
              {treatments.Items.map((item, index) => (
                <Draggable
                  key={`${item.key}`}
                  draggableId={`${item.key}`}
                  index={index}
                  isDragDisabled={isEdit}
                >
                  {(provided) => (
                    <div ref={provided.innerRef} {...provided.draggableProps}>
                      <div
                        className="row mb10 align-items-center"
                        key={item.item_order}
                      >
                        <div className="col-md-6">
                          <span className="time-gray fs12 poppins-medium mb5 mt10 d-inline-flex d-md-none pl25">
                            {t("itemName")}
                          </span>
                          <div className="lineitem-name">
                            <span className="mr15" {...provided.dragHandleProps}>
                              <img
                                src={drag}
                                alt="Drag"
                                className="pt-md-0 pt-0"
                              />
                            </span>
                            <div className="lineitem-name-field">
                              <Input
                                name="Item Name"
                                placeholder={t("itemName")}
                                value={item["Item Name"]}
                                onChange={(e) =>
                                  handleOnChangeItem(
                                    treatmentId,
                                    item.key,
                                    e.target.name,
                                    e.target.value
                                  )
                                }
                                className={`${
                                  item["Item Name"]?.trim() === "" ||
                                  item["Item Name"] === null
                                    ? "bg-invoiceorange"
                                    : ""
                                } ${
                                  item.isNew ? "treatment-addnew-row" : ""
                                }`.trim()}
                                disabled={isEdit}
                              />
                              {environment.APP_ENVIRONMENT !== "production" ? (
                                <Popover
                                  content={<Popup itemId={index} />}
                                  trigger="click"
                                  open={openPopups[index]}
                                >
                                  <Tooltip
                                    title={t("medicine_info")}
                                    placement="bottom"
                                  >
                                    {selectedMedicine === index ? (
                                      <img
                                        className="medicine-info cursor-pointer"
                                        src={selected_question}
                                        alt="question"
                                        onClick={() =>
                                          handleImageClick(
                                            index,
                                            item["Item Name"]
                                          )
                                        }
                                      />
                                    ) : (
                                      <img
                                        className="medicine-info cursor-pointer"
                                        src={ph_question}
                                        alt="selected_question"
                                        onClick={() =>
                                          handleImageClick(
                                            index,
                                            item["Item Name"]
                                          )
                                        }
                                      />
                                    )}
                                  </Tooltip>
                                </Popover>
                              ) : null}
                            </div>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="row">
                            <div className="col-11">
                              <div className="row">
                                <div className="col-md-4">
                                  <span className="time-gray fs12 poppins-medium mb5 mt10 d-inline-flex d-md-none">
                                    {t("quantity")}
                                  </span>
                                  <Input
                                    name={"Item Quantity"}
                                    placeholder={t("quantity")}
                                    value={item["Item Quantity"]}
                                    min={0}
                                    onChange={(e) =>
                                      handleOnChangeItem(
                                        treatmentId,
                                        item.key,
                                        e.target.name,
                                        e.target.value
                                      )
                                    }
                                    className={`${
                                      item["Item Quantity"]?.trim() === "" ||
                                      item["Item Quantity"] === null
                                        ? "bg-invoiceorange"
                                        : ""
                                    } ${
                                      item.isNew ? "treatment-addnew-row" : ""
                                    }`.trim()}
                                    disabled={isEdit}
                                  />
                                </div>
                                <div className="col-md-4">
                                  <span className="time-gray fs12 poppins-medium mb5 mt10 d-inline-flex d-md-none">
                                    {t("prize")}
                                  </span>
                                  <Input
                                    name="Item Price"
                                    type="text"
                                    min={0}
                                    placeholder={t("prize")}
                                    value={item["Item Price"]}
                                    onChange={(e) =>
                                      handleOnChangeItem(
                                        treatmentId,
                                        item.key,
                                        e.target.name,
                                        e.target.value
                                      )
                                    }
                                    onKeyDown={handleKeyDown}
                                    className={`${
                                      item["Item Price"]?.toString()?.trim() ===
                                        "" || item["Item Price"] === null
                                        ? "bg-invoiceorange"
                                        : ""
                                    } ${
                                      item?.isNew ? "treatment-addnew-row" : ""
                                    }`?.trim()}
                                    disabled={isEdit}
                                  />
                                </div>
                                <div className="col-md-4 vat-select-option">
                                  <span className="time-gray fs12 poppins-medium mb5 mt10 d-inline-flex d-md-none">
                                    {t("VAT")}
                                  </span>
                                  <Select
                                    value={item["VAT"]}
                                    name={"VAT"}
                                    placeholder={t("VAT")}
                                    onChange={(e) =>
                                      handleOnChangeVAT(e, item.key)
                                    }
                                    className={`${
                                      item["VAT"]?.trim() === "" ||
                                      item["VAT"] === null
                                        ? "bg-invoiceorange"
                                        : ""
                                    } ${
                                      item.isNew
                                        ? "treatment-addnew-select"
                                        : ""
                                    }`.trim()}
                                    dropdownRender={(menu) => (
                                      <>
                                        {menu}
                                        <div
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            padding: "8px",
                                          }}
                                        >
                                        <Tooltip title={t("customVAT")} placement="bottom">
                                          <Input
                                            style={{ flex: "auto" }}
                                            value={customVAT[item.key]}
                                            onChange={(e) =>
                                              handleCustomInputChange(
                                                e,
                                                item.key
                                              )
                                            }
                                            placeholder={t("customVAT")}
                                            disabled={isEdit}
                                            onKeyDown={(e) =>
                                              handleInputKeyDown(e, item.key)
                                            }
                                          />
                                          </Tooltip>
                                        </div>
                                      </>
                                    )}
                                    options={[
                                      {
                                        value: null,
                                        label: t("none"),
                                      },
                                      {
                                        value: "2.5",
                                        label: "2.5%",
                                      },
                                      {
                                        value: "2.6",
                                        label: "2.6%",
                                      },
                                      {
                                        value: "7.7",
                                        label: "7.7%",
                                      },
                                      {
                                        value: "8.1",
                                        label: "8.1%",
                                      },
                                    ]}
                                    disabled={isEdit}
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="col-1 text-center ps-0 pt8">
                              <Tooltip
                                title={t("deleteItem")}
                                placement="bottom"
                              >
                                <img
                                  src={trash}
                                  alt="trash"
                                  className={`trash-icon ${
                                    !isEdit ? "cursor-pointer" : "is_disabled"
                                  }`}
                                  onClick={(e) =>
                                    handleConfirmationPopup(
                                      "line-item",
                                      treatmentId,
                                      item.key
                                    )
                                  }
                                />
                              </Tooltip>
                            </div>
                          </div>
                        </div>
                      </div>
                      {renderItemClassification(item)}
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </>
  );
};

export default LineItem;
